import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddCircleOutline, CheckCircle, InfoOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { useGetAdminChatBotProfileQuery } from "features/adminUsers/adminUsers";

const API_KEY_EMAIL = "konrad@hivemechanics.io";
const API_KEY_HINT = (
  <>
    To get a chat ID and API key for this user, send an email to{" "}
    <Link href={`mailto:${API_KEY_EMAIL}`} color="inherit" sx={{ fontWeight: 600 }}>
      {API_KEY_EMAIL}
    </Link>{" "}
    and then enter them below.
  </>
);

interface Props {
  userId: number | null;
  isCreateMode?: boolean;
  canEdit?: boolean;
  isCalibrateManager?: boolean;
  chatbotDraft?: { chat_id: string; api_key: string } | null;
  onChatbotDraftChange?: (
    data: { chat_id: string; api_key: string },
    serverProfile: { chat_id?: string; api_key?: string } | null | undefined,
  ) => void;
  onSuccess?: (message: string, severity: "success" | "error") => void;
  onSaveComplete?: () => void;
  onSaveAll?: () => void;
  onCreateUser?: () => void;
  createUserDisabled?: boolean;
  hasAnyChanges?: boolean;
  isSaving?: boolean;
}

export default function UserManagementChatBotTab({
  userId,
  isCreateMode = false,
  canEdit = true,
  isCalibrateManager = false,
  chatbotDraft,
  onChatbotDraftChange,
  onSuccess,
  onSaveComplete,
  onSaveAll,
  onCreateUser,
  createUserDisabled = false,
  hasAnyChanges = false,
  isSaving = false,
}: Props) {
  const { data: profile, isLoading, isError, error } = useGetAdminChatBotProfileQuery(userId!, { skip: !userId || isCreateMode });

  const [chatId, setChatId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [syncedFromProfile, setSyncedFromProfile] = useState(false);

  const noProfile = isCreateMode || (isError && (error as { status?: number })?.status === 404);
  const hasProfile = !isCreateMode && !isError && !!profile;
  const canSeeApiKey = !isCalibrateManager;

  useEffect(() => {
    setSyncedFromProfile(false);
  }, [userId]);

  useEffect(() => {
    if (chatbotDraft !== undefined && chatbotDraft !== null) {
      setChatId(chatbotDraft.chat_id ?? "");
      setApiKey(chatbotDraft.api_key ?? "");
      setSyncedFromProfile(true);
    } else if (hasProfile && profile) {
      setChatId(profile.chat_id ?? "");
      setApiKey(profile.api_key ?? "");
      setSyncedFromProfile(true);
    } else {
      setSyncedFromProfile(false);
    }
  }, [userId, hasProfile, profile?.chat_id, profile?.api_key, chatbotDraft]);

  const handleChatIdChange = (value: string) => {
    setChatId(value);
    onChatbotDraftChange?.(
      { chat_id: value, api_key: apiKey },
      profile ? { chat_id: profile.chat_id, api_key: profile.api_key } : null,
    );
  };
  const handleApiKeyChange = (value: string) => {
    setApiKey(value);
    onChatbotDraftChange?.(
      { chat_id: chatId, api_key: value },
      profile ? { chat_id: profile.chat_id, api_key: profile.api_key } : null,
    );
  };

  if (isLoading && !isCreateMode) {
    return (
      <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} />
      </Box>
    );
  }

  const hasChanges =
    syncedFromProfile &&
    ((chatId || profile?.chat_id) !== profile?.chat_id ||
      (canSeeApiKey && apiKey !== (profile?.api_key ?? "")));

  return (
    <Box sx={{ px: 2, pt: 2, pb: 2, display: "flex", flexDirection: "column", flex: 1 }}>
      {(noProfile || hasProfile) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 2,
            color: hasProfile ? "success.main" : "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          {hasProfile ? (
            <>
              <CheckCircle sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="success.main" fontWeight={600}>
                Profile saved — you can edit the config below
              </Typography>
            </>
          ) : (
            <>
              <AddCircleOutline sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                First-time setup — no AI Agent profile yet
              </Typography>
            </>
          )}
        </Box>
      )}

      <Alert icon={<InfoOutlined />} severity="info" sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
        {API_KEY_HINT}
      </Alert>

      {noProfile && (
        <>
          <TextField
            inputProps={{ autoComplete: "off" }}
            label="Chat ID"
            value={chatId}
            onChange={(e) => canEdit && handleChatIdChange(e.target.value)}
            placeholder="pending"
            size="small"
            fullWidth
            disabled={!canEdit}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
          {canSeeApiKey && (
            <TextField
              label="API Key"
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => canEdit && handleApiKeyChange(e.target.value)}
              size="small"
              fullWidth
              disabled={!canEdit}
              inputProps={{ autoComplete: "new-password" }}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowApiKey((v) => !v)} edge="end">
                      {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          {isCreateMode && onCreateUser ? (
            <Box sx={{ mt: "auto", pt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={onCreateUser}
                disabled={createUserDisabled || isSaving}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
              >
                {isSaving ? "Creating…" : "Create User"}
              </Button>
            </Box>
          ) : (
            !isCreateMode &&
            onSaveAll && (
              <Box sx={{ mt: "auto", pt: 2 }}>
                {hasAnyChanges && (
                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1, color: "#ad1457" }}>
                    Unsaved changes
                  </Typography>
                )}
                <Tooltip title={!hasAnyChanges ? "No changes to save" : ""}>
                  <span>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={onSaveAll}
                      disabled={!canEdit || !hasAnyChanges || isSaving}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
                    >
                      {isSaving ? "Saving…" : "Save Changes"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )
          )}
        </>
      )}

      {hasProfile && profile && (
        <>
          <TextField
            label="Chat ID"
            value={chatId || profile.chat_id}
            onChange={(e) => canEdit && handleChatIdChange(e.target.value)}
            size="small"
            fullWidth
            disabled={!canEdit}
            inputProps={{ autoComplete: "off" }}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
          {canSeeApiKey && (
            <TextField
              label="API Key"
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => canEdit && handleApiKeyChange(e.target.value)}
              placeholder="Enter API key"
              size="small"
              fullWidth
              disabled={!canEdit}
              inputProps={{ autoComplete: "new-password" }}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowApiKey((v) => !v)} edge="end">
                      {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          {isCalibrateManager && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              API Key is hidden for Calibrate Managers.
            </Typography>
          )}
          <Box sx={{ mt: "auto" }}>
            {hasAnyChanges && (
              <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1, color: "#ad1457" }}>
                Unsaved changes
              </Typography>
            )}
            {isCreateMode && onCreateUser ? (
              <Button
                variant="contained"
                fullWidth
                onClick={onCreateUser}
                disabled={createUserDisabled || isSaving}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
              >
                {isSaving ? "Creating…" : "Create User"}
              </Button>
            ) : (
              !isCreateMode &&
              onSaveAll && (
                <Tooltip title={!hasAnyChanges ? "No changes to save" : ""}>
                  <span>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={onSaveAll}
                      disabled={!canEdit || !hasAnyChanges || isSaving}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
                    >
                      {isSaving ? "Saving…" : "Save Changes"}
                    </Button>
                  </span>
                </Tooltip>
              )
            )}
          </Box>
        </>
      )}

      {!noProfile && !hasProfile && !isLoading && (
        <Typography variant="body2" color="text.secondary">
          Unable to load AI Agent profile. Please try again.
        </Typography>
      )}
    </Box>
  );
}
